<script setup>
import { ref, onMounted } from 'vue';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { setColorTheme } from 'dashboard/helper/themeHelper.js';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  label: {
    type: String,
    default: 'Theme Mode',
  },
  description: {
    type: String,
    default: 'Choose your preferred color theme.',
  },
});

const currentTheme = ref('mix');

const themeOptions = [
  {
    key: 'light',
    label: 'Light',
    icon: 'i-ph-sun-bold',
  },
  {
    key: 'dark',
    label: 'Dark',
    icon: 'i-ph-moon-bold',
  },
  {
    key: 'mix',
    label: 'Mix',
    icon: 'i-ph-squares-four-bold',
  },
  {
    key: 'auto',
    label: 'System',
    icon: 'i-ph-desktop-bold',
  },
];

const loadTheme = () => {
  const savedTheme = LocalStorage.get(LOCAL_STORAGE_KEYS.COLOR_SCHEME) || 'mix';
  currentTheme.value = savedTheme;
};

const setTheme = theme => {
  currentTheme.value = theme;
  LocalStorage.set(LOCAL_STORAGE_KEYS.COLOR_SCHEME, theme);
  const isOSOnDarkMode = window.matchMedia(
    '(prefers-color-scheme: dark)'
  ).matches;
  setColorTheme(isOSOnDarkMode);

  const themeLabels = {
    light: 'Light',
    dark: 'Dark',
    mix: 'Mix',
    auto: 'System',
  };
  useAlert(`Theme changed to ${themeLabels[theme]} mode`);
};

onMounted(() => {
  loadTheme();
});
</script>

<template>
  <div class="theme-toggle">
    <div class="flex items-center justify-between">
      <div>
        <h4 class="text-sm font-medium text-n-slate-12">{{ label }}</h4>
        <p class="text-xs text-n-slate-11 mt-0.5">{{ description }}</p>
      </div>
      <div class="theme-options flex gap-1 bg-n-alpha-2 p-1 rounded-xl">
        <button
          v-for="option in themeOptions"
          :key="option.key"
          class="theme-btn"
          :class="{ active: currentTheme === option.key }"
          @click="setTheme(option.key)"
        >
          <span class="size-4" :class="[option.icon]" />
          <span class="text-xs font-medium">{{ option.label }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.theme-toggle {
  padding: 0;
}

.theme-options {
  border: 1px solid var(--wlab-border-default);
}

.theme-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: var(--wlab-text-muted);
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    color 0.2s ease;
}

.theme-btn:hover {
  color: var(--wlab-text-primary);
  background: rgba(255, 255, 255, 0.05);
}

.theme-btn.active {
  background: var(--wlab-accent);
  color: white;
  box-shadow: 0 2px 8px rgba(249, 115, 22, 0.25);
}

/* Light mode adjustments */
:global(body:not(.dark)) .theme-btn:hover {
  background: rgba(0, 0, 0, 0.05);
}
</style>
